import { useState } from 'react';

export default function ProjectGallery() {
  const [activeTab, setActiveTab] = useState('all');
  const [lightbox, setLightbox] = useState<string | null>(null);

  const projects = [
    {
      category: 'fabrication',
      title: 'Our Shop',
      description: 'Where the work gets done — fully equipped fabrication shop',
      image: 'https://i.ibb.co/HDPjYM87/IMG-20260214-181603058-HDR.jpg'
    },
    {
      category: 'fabrication',
      title: 'Miller Bobcat Welder Setup',
      description: 'Professional welding equipment ready for any job',
      image: 'https://i.ibb.co/7JchS7Q3/IMG-20260214-181541737-HDR.jpg'
    },
    {
      category: 'fabrication',
      title: 'Custom Steel Frame Build',
      description: 'Trailer frame fabrication in progress — built to spec',
      image: 'https://i.ibb.co/k2P9N792/IMG-20260214-125959750-HDR.jpg'
    },
    {
      category: 'fabrication',
      title: 'Steel Fabrication Work',
      description: 'Precision metalwork and custom fabrication',
      image: 'https://i.ibb.co/MxzRhbr5/20220927-145247.jpg'
    },
    {
      category: 'fabrication',
      title: 'Custom Build in Progress',
      description: 'Hands-on craftsmanship on every project',
      image: 'https://i.ibb.co/LdBW0Jzc/20220928-131405.jpg'
    },
    {
      category: 'fabrication',
      title: 'Shop Fabrication',
      description: 'Quality steel work built to last',
      image: 'https://i.ibb.co/My222Nx5/20220927-160030.jpg'
    },
    {
      category: 'fabrication',
      title: 'Welding Project',
      description: 'Professional welds on structural metalwork',
      image: 'https://i.ibb.co/93rmmdtQ/20220928-135401.jpg'
    },
    {
      category: 'fabrication',
      title: 'Metal Structure Build',
      description: 'Heavy-duty fabrication from the ground up',
      image: 'https://i.ibb.co/jvdS9Qxh/20221024-113947.jpg'
    },
    {
      category: 'repair',
      title: 'Structural Repair',
      description: 'Reinforced and restored to full strength',
      image: 'https://i.ibb.co/0RW8jRqL/20220928-120720.jpg'
    },
    {
      category: 'commercial',
      title: 'Commercial Welding',
      description: 'Large-scale commercial metalwork completed on time',
      image: 'https://i.ibb.co/jnwWdm8/20220409-083823.jpg'
    },
    {
      category: 'fabrication',
      title: 'Custom Frame Assembly',
      description: 'Precision-fit frame components welded to spec',
      image: 'https://i.ibb.co/23M0Jmcb/20220927-145230.jpg'
    },
    {
      category: 'commercial',
      title: 'Industrial Project',
      description: 'Heavy industrial fabrication and installation',
      image: 'https://i.ibb.co/3mPggNnJ/20220409-091157.jpg'
    },
    {
      category: 'repair',
      title: 'Equipment Restoration',
      description: 'Bringing worn equipment back to working condition',
      image: 'https://i.ibb.co/wVDjps4/20220409-033613.jpg'
    },
    {
      category: 'repair',
      title: 'Weld Repair & Reinforcement',
      description: 'Structural weld repairs done right the first time',
      image: 'https://i.ibb.co/YTZyR3gz/20220409-033617.jpg'
    },
    {
      category: 'fabrication',
      title: 'Fabrication Detail',
      description: 'Close-up craftsmanship — every weld matters',
      image: 'https://i.ibb.co/HTPLZ1GJ/20220927-145230.jpg'
    },
  ];

  const tabs = [
    { id: 'all', label: 'All Projects', icon: 'ri-grid-line' },
    { id: 'repair', label: 'Emergency Repairs', icon: 'ri-tools-line' },
    { id: 'fabrication', label: 'Custom Fabrication', icon: 'ri-hammer-line' },
    { id: 'commercial', label: 'Commercial Work', icon: 'ri-building-line' }
  ];

  const filteredProjects = activeTab === 'all'
    ? projects
    : projects.filter(p => p.category === activeTab);

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Our Work <span className="text-orange-500">in the Field</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Real projects, real results. See the quality and precision we deliver on every job.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`whitespace-nowrap px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-300 cursor-pointer flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'bg-orange-500 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${tab.icon} text-lg`}></i>
              </div>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Photo Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project, index) => (
            <div
              key={index}
              onClick={() => setLightbox(project.image)}
              className="bg-gray-50 rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer group"
            >
              <div className="relative h-64 w-full overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'https://public.readdy.ai/ai/img_res/314f4d77ab8fe91a2f00e0de2c6d12ba.jpg';
                  }}
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                  <div className="w-10 h-10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/80 rounded-full">
                    <i className="ri-zoom-in-line text-gray-800 text-xl"></i>
                  </div>
                </div>
              </div>
              <div className="p-5">
                <h3 className="text-lg font-bold text-gray-900 mb-1">{project.title}</h3>
                <p className="text-sm text-gray-500">{project.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <p className="text-gray-500 text-sm">
            Every project showcases our commitment to quality, durability, and precision welding
          </p>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6 cursor-pointer"
          onClick={() => setLightbox(null)}
        >
          <div className="relative max-w-4xl w-full">
            <img
              src={lightbox}
              alt="Project"
              className="w-full max-h-[80vh] object-contain rounded-lg"
            />
            <button
              className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center bg-white/20 hover:bg-white/40 rounded-full text-white transition cursor-pointer"
              onClick={() => setLightbox(null)}
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
